import { useEffect, useState } from "react";
import { Check, X, AlertCircle, Trash2 } from "lucide-react";
import { DETAIL_BAR_TOAST_BOTTOM } from "./DetailActionBar";

export type ToastType = "success" | "error" | "info";
export type ToastVariant = "default" | "dark-bottom";

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
  action?: ToastAction;
  variant?: ToastVariant;
  icon?: "trash" | "check";
  /**
   * `dark-bottom` のときだけ効く。画面の下に操作の帯（`DetailActionBar`）がある画面では `true` にして、
   * 帯の上に出す（PRODUCT_DEV-1044 10/05。帯の「ToDoを新規作成」に黒いラベルが被さって押せなかった）。
   */
  aboveActionBar?: boolean;
}

/**
 * モバイル通知風トーストメッセージコンポーネント
 *
 * @example
 * ```tsx
 * const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
 *
 * // 表示
 * setToast({ message: "コピーしました", type: "success" });
 *
 * // アクションボタン付き表示
 * setToast({
 *   message: "商談記録をゴミ箱へ移動しました",
 *   type: "success",
 *   action: { label: "元に戻す", onClick: handleUndo },
 *   duration: 5000,
 * });
 *
 * // 下部・透過黒背景バリアント
 * setToast({
 *   message: "商談記録をゴミ箱に移動しました。",
 *   variant: "dark-bottom",
 *   icon: "trash",
 *   action: { label: "元に戻す", onClick: handleUndo },
 *   duration: 5000,
 * });
 * ```
 */
export const Toast: React.FC<ToastProps> = ({
  message,
  type = "info",
  duration = 3000,
  onClose,
  action,
  variant = "default",
  icon,
  aboveActionBar = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // マウント時にスライドイン
    const showTimer = setTimeout(() => setIsVisible(true), 10);

    let closeTimer: ReturnType<typeof setTimeout> | undefined;
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
      closeTimer = setTimeout(onClose, 400); // アニメーション完了後にクローズ
    }, duration);

    // 3つとも外す。残すと外れた後に状態更新が走り、テストでは jsdom の片付け後に
    // `window is not defined` で落ちる（2026-10-06 本番リリースの Fanout、とちぎん unizo）
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      clearTimeout(closeTimer);
    };
  }, [duration, onClose]);

  if (variant === "dark-bottom") {
    return (
      <div
        // 帯（DetailActionBar）の上に出す位置は帯の側が正本（DETAIL_BAR_TOAST_BOTTOM）
        className={`fixed ${aboveActionBar ? DETAIL_BAR_TOAST_BOTTOM : "bottom-20"} left-0 right-0 z-50 px-4 pointer-events-none transition-all duration-400 ease-out ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
        }`}
      >
        <div className="max-w-md mx-auto bg-black/80 rounded-2xl overflow-hidden pointer-events-auto">
          <div className="flex items-center gap-3 px-4 py-3">
            {icon === "trash" && (
              <Trash2 size={18} className="text-white flex-shrink-0" />
            )}
            {icon === "check" && (
              <Check size={18} className="text-white flex-shrink-0" />
            )}
            <p className="flex-1 text-white text-sm">
              {message}
            </p>
            {action && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                  setIsVisible(false);
                  setTimeout(onClose, 400);
                }}
                className="text-white font-bold text-sm px-2 py-1 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0 underline"
              >
                {action.label}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const getIcon = () => {
    switch (type) {
      case "success":
        return <Check size={18} className="text-white flex-shrink-0" />;
      case "error":
        return <X size={18} className="text-white flex-shrink-0" />;
      case "info":
        return <AlertCircle size={18} className="text-white flex-shrink-0" />;
    }
  };

  const getIconBackground = () => {
    switch (type) {
      case "success":
        return "bg-primary";
      case "error":
        return "bg-danger";
      case "info":
        return "bg-white/20";
    }
  };

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 px-4 pt-4 pointer-events-none transition-all duration-400 ease-out ${
        isVisible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
      }`}
    >
      {/* PRODUCT_DEV-783: 画面が白地なので、お知らせは黒地にそろえる（下に出す dark-bottom と同じ色） */}
      <div className="max-w-md mx-auto bg-black/80 rounded-2xl shadow-2xl overflow-hidden pointer-events-auto">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className={`${getIconBackground()} rounded-full p-1.5`}>
            {getIcon()}
          </div>
          <div className="flex-1">
            <p className="text-white font-medium text-sm leading-relaxed">
              {message}
            </p>
          </div>
          {action && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
                setIsVisible(false);
                setTimeout(onClose, 400);
              }}
              className="text-white font-bold text-sm px-2 py-1 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0 underline"
            >
              {action.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
